import {
    ActiveFace,
    CabinetFace,
    Direction,
    facesFrontOrBack,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {Box3, Vector3} from 'three';

interface SnappingParams {
    isCabinetProductFiller: boolean;
    oFace: CabinetFace;
    sFace: CabinetFace;
    activeFace: ActiveFace;
    targetPos: Vector3;
    otherCenterAlong: number;
    selectedCenterAlong: number;
    selectedCabinetDirection: Direction;
    otherBox: Box3;
    selectedSizeAlong: number;
    axis: 'x' | 'z';
    selectedCenterAcross: number;
}

/**
 * The snapped position along one axis for a cabinet or filler product facing
 * the other product. Values named Along are measured on that axis, and
 * selectedCenterAcross on the other plan axis.
 *
 * @param {SnappingParams} params - The parameters for snapping calculation.
 * @return {number|null} The calculated position or null if snapping should be skipped.
 */
export function calculateBestSnapped(params: SnappingParams): number | null {
    const {
        isCabinetProductFiller,
        oFace,
        sFace,
        activeFace,
        targetPos,
        otherCenterAlong,
        selectedCenterAlong,
        selectedCabinetDirection,
        otherBox,
        selectedSizeAlong,
        axis,
        selectedCenterAcross,
    } = params;
    const isX = axis === 'x';

    if (isCabinetProductFiller) {
        const activeFaceCheck = isX
            ? activeFace.leftRight.x
            : activeFace.frontBack.z;
        const oFaceNormalValue = isX ? oFace.normal.x : oFace.normal.z;
        const targetPosValue = isX ? targetPos.x : targetPos.z;
        if (oFaceNormalValue !== activeFaceCheck) {
            return null;
        }

        const isNormalOrBack = facesFrontOrBack(selectedCabinetDirection);

        const inRange = isNormalOrBack
            ? selectedCenterAcross < otherBox.max.z &&
              selectedCenterAcross > otherBox.min.z
            : selectedCenterAcross < otherBox.max.x &&
              selectedCenterAcross > otherBox.min.x;

        if (!inRange) return null;

        const axisSign = isNormalOrBack
            ? activeFace.leftRight.x
            : activeFace.frontBack.z;

        if (axisSign === 1 && selectedCenterAlong >= oFace.position) {
            return targetPosValue + (oFace.position - sFace.position);
        }

        if (axisSign === -1 && selectedCenterAlong <= oFace.position) {
            return targetPosValue + (oFace.position - sFace.position);
        }

        return null;
    }

    const otherMin = isX ? otherBox.min.x : otherBox.min.z;
    const otherMax = isX ? otherBox.max.x : otherBox.max.z;

    if (selectedCenterAlong > otherCenterAlong) {
        return selectedCabinetDirection === 'normal' ||
            selectedCabinetDirection === 'left'
            ? otherMax
            : otherMax + selectedSizeAlong;
    } else {
        return selectedCabinetDirection === 'normal' ||
            selectedCabinetDirection === 'left'
            ? otherMin - selectedSizeAlong
            : otherMin;
    }
}
